import { ReactNode } from "react";
import { Box, ButtonBase, Typography } from "@mui/material";
import { Check } from "@mui/icons-material";

export interface RadioCardCotizadorOption {
  value: string;
  label: string;
  sub: string;
  icon: ReactNode;
  testId: string;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  options: RadioCardCotizadorOption[];
  columns: number;
}

/**
 * Replica `.radio-card` del cotizador en el diseño: ícono en círculo de 44px, label + descripción y
 * un check a la derecha. La opción elegida se pinta con `primary.accent` sobre `primary.base`.
 */
export const RadioCardGroupCotizador = ({ value, onChange, options, columns }: Props) => (
  <Box role="radiogroup" sx={{ display: "grid", gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: 1.5 }}>
    {options.map((opt) => {
      const on = value === opt.value;
      return (
        <ButtonBase
          key={opt.value}
          role="radio"
          aria-checked={on}
          onClick={() => onChange(opt.value)}
          data-testid={opt.testId}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            textAlign: "left",
            gap: 2,
            p: "18px 20px",
            border: "1.5px solid",
            borderColor: on ? "primary.accent" : "#E4E7EC",
            borderRadius: "8px",
            bgcolor: on ? "primary.base" : "background.paper",
            boxShadow: (theme) => (on ? `0 0 0 1px ${theme.palette.primary.accent} inset` : "none"),
            transition: "border-color .18s ease, background-color .18s ease",
            "&:hover": { borderColor: "primary.accent", bgcolor: "primary.base" },
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              width: 44,
              height: 44,
              borderRadius: "50%",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: on ? "primary.accent" : "#F2F5F9",
              color: on ? "#fff" : "text.secondary",
              border: "1px solid",
              borderColor: on ? "primary.accent" : "#E4E7EC",
              transition: "background-color .2s ease, color .2s ease",
            }}
          >
            {opt.icon}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 14.5, fontWeight: 600, letterSpacing: "-0.01em", color: "text.primary" }}>
              {opt.label}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {opt.sub}
            </Typography>
          </Box>
          <Box
            sx={{
              flexShrink: 0,
              width: 22,
              height: 22,
              borderRadius: "50%",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: on ? "primary.accent" : "#F2F5F9",
              border: "1.5px solid",
              borderColor: on ? "primary.accent" : "#D5DCE4",
              color: on ? "#fff" : "transparent",
            }}
          >
            <Check sx={{ fontSize: 14 }} />
          </Box>
        </ButtonBase>
      );
    })}
  </Box>
);
